import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { getWsBaseUrl } from "../utils/wsUrl";
import "./MessageLogLayer.css";
import "./ListPage.css";

const FILTER_LABELS = {
    "1": "Sent",
    "0": "Failed / Invalid",
    "2": "In queue",
};

const parseLogText = (val) => {
    if (!val) return "";
    try {
        const parsed = typeof val === "string" ? JSON.parse(val) : val;
        if (parsed && typeof parsed === "object") {
            return String(parsed.text || parsed.message || "");
        }
        return String(val);
    } catch {
        return String(val);
    }
};

const formatMessageHtml = (text) => {
    const escaped = String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    return escaped
        .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
        .replace(/_(.*?)_/g, "<em>$1</em>")
        .replace(
            /(https?:\/\/[^\s<]+)/g,
            '<a href="$1" target="_blank" rel="noreferrer">$1</a>'
        )
        .replace(/\n/g, "<br />");
};

const formatShortDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const LogTextPreview = ({ text, onView }) => {
    const full = String(text || "").trim();
    if (!full) return <span className="text-muted">—</span>;

    return (
        <div className="log-text-cell">
            <p className="log-text-preview">{full}</p>
            <button type="button" className="log-text-view-btn" onClick={() => onView(full)}>
                <Icon icon="mdi:eye-outline" width={14} />
                View
            </button>
        </div>
    );
};

const LogReasonPreview = ({ text, onView }) => {
    const full = String(text || "").trim();
    if (!full) return <span className="log-reason-cell empty">—</span>;

    const needsMore = full.length > 48;

    return (
        <div className="log-reason-wrap">
            <span className="log-reason-cell" title={full}>
                {needsMore ? `${full.slice(0, 48)}…` : full}
            </span>
            {needsMore && (
                <button
                    type="button"
                    className="log-text-view-btn"
                    onClick={() => onView(full)}
                >
                    Show more
                </button>
            )}
        </div>
    );
};

const TextPreviewModal = ({ text, title = "Preview", onClose }) => {
    if (!text) return null;

    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            /* ignore */
        }
    };

    return (
        <div className="log-msg-modal-backdrop" onClick={onClose}>
            <div className="log-msg-modal" onClick={(e) => e.stopPropagation()}>
                <div className="log-msg-modal-header">
                    <h6>{title}</h6>
                    <button
                        type="button"
                        className="log-msg-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <Icon icon="mdi:close" width={18} />
                    </button>
                </div>
                <div className="log-msg-bubble">
                    <div
                        className="log-msg-bubble-body"
                        dangerouslySetInnerHTML={{ __html: formatMessageHtml(text) }}
                    />
                </div>
                <div className="log-msg-modal-actions">
                    <button type="button" className="log-msg-action" onClick={copyText}>
                        <Icon icon="mdi:content-copy" width={16} />
                        Copy
                    </button>
                    <button type="button" className="log-msg-action primary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const MessageLogLayer = () => {
    const [sentCount, setSentCount] = useState(0);
    const [queueCount, setQueueCount] = useState(0);
    const [invalidCount, setInvalidCount] = useState(0);
    const [isSpinning, setIsSpinning] = useState(false);
    const [currentFilter, setCurrentFilter] = useState("");
    const [liveLogs, setLiveLogs] = useState([]);
    const [serverLogs, setServerLogs] = useState([]);
    const [serverTotal, setServerTotal] = useState(0);
    const [wsConnected, setWsConnected] = useState(false);
    const [liveMode, setLiveMode] = useState(true);
    const [preview, setPreview] = useState(null); // { text, title }

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const searchTimer = useRef(null);

    const currentFilterRef = useRef(currentFilter);
    const pageRef = useRef(1);
    const limitRef = useRef(10);
    const searchRef = useRef("");
    const fetchRef = useRef(null);
    const liveModeRef = useRef(true);

    useEffect(() => {
        currentFilterRef.current = currentFilter;
    }, [currentFilter]);

    useEffect(() => {
        liveModeRef.current = liveMode;
    }, [liveMode]);

    const applyCountsFromSocket = (payload) => {
        if (payload.sent_count != null) setSentCount(Number(payload.sent_count) || 0);
        if (payload.invalid_count != null) setInvalidCount(Number(payload.invalid_count) || 0);
        if (payload.queue_count != null) setQueueCount(Number(payload.queue_count) || 0);
    };

    const normalizeStatus = (status) => {
        const s = String(status ?? "").trim().toUpperCase();
        if (s === "1" || s === "SENT" || s === "SUCCESS" || s === "DELIVERED") return "1";
        if (s === "0" || s === "FAILED" || s === "INVALID" || s === "ERROR") return "0";
        if (s === "2" || s === "QUEUE" || s === "QUEUED" || s === "PENDING") return "2";
        return String(status ?? "");
    };

    const matchesFilter = (log, filter) => {
        if (!filter) return true;
        return normalizeStatus(log.status) === normalizeStatus(filter);
    };

    const fetchMessageLogs = useCallback(
        async (pageArg = 1, limitArg = 10, searchArg = "", filterArg) => {
            const statusFilter =
                filterArg !== undefined ? filterArg : currentFilterRef.current;
            pageRef.current = pageArg;
            limitRef.current = limitArg;
            searchRef.current = searchArg || "";

            try {
                setIsSpinning(true);

                const queryParams = new URLSearchParams();
                if (pageArg) queryParams.append("page", pageArg);
                if (limitArg) queryParams.append("limit", limitArg);
                if (searchArg) queryParams.append("search", searchArg);
                if (statusFilter !== "" && statusFilter != null) {
                    queryParams.append("status", statusFilter);
                }

                // Send status in query + body so backends that read either still filter
                const response = await apiCall({
                    url: `user/get-messages-log?${queryParams.toString()}`,
                    method: "POST",
                    body: {
                        type: "single",
                        ...(statusFilter !== "" && statusFilter != null
                            ? { status: statusFilter, status_filter: statusFilter }
                            : {}),
                    },
                });

                let rows = Array.isArray(response?.data) ? response.data : [];
                let total = Number(response?.total);
                if (!Number.isFinite(total)) total = rows.length || 0;

                if (response.sent_count != null) setSentCount(response.sent_count || 0);
                if (response.queue_count != null) setQueueCount(response.queue_count || 0);
                if (response.invalid_count != null) setInvalidCount(response.invalid_count || 0);

                // If filtering queue/sent/failed and API ignored status, client-filter as fallback
                if (statusFilter !== "" && statusFilter != null && rows.length > 0) {
                    const filtered = rows.filter((r) => matchesFilter(r, statusFilter));
                    // Only apply client filter when mixed statuses come back (API ignored filter)
                    const hasOther = rows.some((r) => !matchesFilter(r, statusFilter));
                    if (hasOther && filtered.length > 0) {
                        rows = filtered;
                        total = filtered.length;
                    }
                }

                if (!statusFilter && pageArg === 1) {
                    setLiveLogs(rows);
                }

                setServerLogs(rows);
                setServerTotal(total);
                setPage(pageArg);

                return response;
            } catch (error) {
                console.error("Error fetching message logs:", error);
                setServerLogs([]);
                setServerTotal(0);
                return { data: [], total: 0 };
            } finally {
                setIsSpinning(false);
            }
        },
        []
    );

    fetchRef.current = fetchMessageLogs;

    const openFilter = (status) => {
        setCurrentFilter(status);
        setLiveMode(false);
        setSearch("");
        setSearchInput("");
        setPage(1);
        fetchMessageLogs(1, limit, "", status);
    };

    const clearFilter = () => {
        setCurrentFilter("");
        setLiveMode(true);
        setSearch("");
        setSearchInput("");
        setPage(1);
        fetchMessageLogs(1, limit, "", "");
    };

    const removeMessageLog = async (selected, type = null) => {
        try {
            const response = await apiCall({
                url: `user/del-logs`,
                method: "POST",
                body: { selected, type },
            });
            if (response.success) {
                fetchRef.current?.(
                    pageRef.current,
                    limitRef.current,
                    searchRef.current,
                    currentFilterRef.current
                );
            }
        } catch (error) {
            console.error("Error deleting logs:", error);
        }
    };

    const columns = [
        {
            key: "instance_name",
            label: "Instance",
            className: "col-instance",
            render: (val) => <span className="fw-semibold text-sm">{val || "—"}</span>,
        },
        {
            key: "mobile",
            label: "Mobile",
            className: "col-mobile",
            render: (val) => (
                <span className="text-sm log-mobile-cell" title={val || ""}>
                    {val || "—"}
                </span>
            ),
        },
        {
            key: "content",
            label: "Message",
            className: "col-text",
            render: (val) => (
                <LogTextPreview
                    text={parseLogText(val)}
                    onView={(full) => setPreview({ text: full, title: "Message preview" })}
                />
            ),
        },
        {
            key: "status",
            label: "Status",
            className: "col-status",
            render: (val) => {
                const n = normalizeStatus(val);
                if (n === "2") return <span className="list-status info">Queue</span>;
                if (n === "1") return <span className="list-status success">Sent</span>;
                return <span className="list-status danger">Failed</span>;
            },
        },
        {
            key: "reason",
            label: "Reason",
            className: "col-reason",
            render: (val) => (
                <LogReasonPreview
                    text={val}
                    onView={(full) => setPreview({ text: full, title: "Failure reason" })}
                />
            ),
        },
        {
            key: "received_datetime",
            label: "Received",
            className: "col-date",
            render: (val) => (
                <span className="log-date-cell" title={val ? new Date(val).toLocaleString() : ""}>
                    {formatShortDate(val)}
                </span>
            ),
        },
        {
            key: "createdAt",
            label: "Sent",
            className: "col-date",
            render: (val) => (
                <span className="log-date-cell" title={val ? new Date(val).toLocaleString() : ""}>
                    {formatShortDate(val)}
                </span>
            ),
        },
        {
            key: "actions",
            label: "",
            className: "col-actions",
            render: (val, row) => (
                <button
                    type="button"
                    title="Delete log"
                    onClick={() => removeMessageLog([row.id])}
                    className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                >
                    <Icon icon="mingcute:delete-2-line" />
                </button>
            ),
        },
    ];

    useEffect(() => {
        document.title = "Message Logs";

        const wsBase = getWsBaseUrl();
        let socket;
        let pingInterval;
        let reconnectTimer;
        let isUnmounted = false;

        const connectWebSocket = () => {
            socket = new WebSocket(wsBase);

            socket.onopen = () => {
                setWsConnected(true);
                const token = localStorage.getItem("token");
                if (token) socket.send(JSON.stringify({ type: "auth", token }));
                pingInterval = setInterval(() => {
                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({ type: "ping" }));
                    }
                }, 30000);
            };

            socket.onmessage = (event) => {
                let eventData;
                try {
                    eventData = JSON.parse(event.data);
                } catch {
                    return;
                }

                if (
                    ["pong", "auth_ok", "auth_error", "chat_message", "conversation_updated", "message_log"].includes(
                        eventData.type
                    )
                ) {
                    return;
                }

                if (eventData.type !== "log_stats") return;

                applyCountsFromSocket(eventData);

                if (eventData.refresh) {
                    fetchRef.current?.(
                        pageRef.current,
                        limitRef.current,
                        searchRef.current,
                        currentFilterRef.current
                    );
                    return;
                }

                if (eventData.log && liveModeRef.current) {
                    const filter = currentFilterRef.current;
                    if (!matchesFilter(eventData.log, filter)) return;

                    setLiveLogs((prev) => {
                        if (prev.some((row) => row.id && row.id === eventData.log.id)) {
                            return prev;
                        }
                        return [eventData.log, ...prev].slice(0, 200);
                    });
                }
            };

            socket.onerror = () => setWsConnected(false);
            socket.onclose = () => {
                setWsConnected(false);
                clearInterval(pingInterval);
                if (!isUnmounted) reconnectTimer = setTimeout(connectWebSocket, 5000);
            };
        };

        connectWebSocket();
        fetchMessageLogs(1, 10, "", "");

        return () => {
            isUnmounted = true;
            clearInterval(pingInterval);
            clearTimeout(reconnectTimer);
            if (searchTimer.current) clearTimeout(searchTimer.current);
            if (socket && socket.readyState === WebSocket.OPEN) socket.close();
        };
    }, [fetchMessageLogs]);

    const handleSearchChange = (value) => {
        setSearchInput(value);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setSearch(value);
            setPage(1);
            if (!liveMode) {
                fetchMessageLogs(1, limit, value, currentFilter);
            }
        }, 350);
    };

    const handlePageChange = (nextPage) => {
        setPage(nextPage);
        if (!liveMode) {
            fetchMessageLogs(nextPage, limit, search, currentFilter);
        }
    };

    const handleLimitChange = (nextLimit) => {
        setLimit(nextLimit);
        setPage(1);
        if (!liveMode) {
            fetchMessageLogs(1, nextLimit, search, currentFilter);
        }
    };

    const displayRows = liveMode
        ? liveLogs.filter((log) => {
              if (!matchesFilter(log, currentFilter)) return false;
              if (!searchInput?.trim()) return true;
              const q = searchInput.toLowerCase();
              return (
                  log.instance_name?.toLowerCase().includes(q) ||
                  log.mobile?.toLowerCase().includes(q) ||
                  log.reason?.toLowerCase().includes(q) ||
                  String(log.content || "").toLowerCase().includes(q)
              );
          })
        : serverLogs;

    const totalRecords = liveMode ? displayRows.length : serverTotal;
    const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / limit) || 1);
    const safePage = Math.min(Math.max(1, page), totalPages);
    const pageRows = liveMode
        ? displayRows.slice((safePage - 1) * limit, safePage * limit)
        : displayRows;
    const startIndex = totalRecords === 0 ? 0 : (safePage - 1) * limit + 1;
    const endIndex = Math.min(safePage * limit, totalRecords);

    const pageButtons = (() => {
        const max = 7;
        if (totalPages <= max) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const pages = new Set(
            [1, totalPages, safePage, safePage - 1, safePage + 1].filter(
                (p) => p >= 1 && p <= totalPages
            )
        );
        return Array.from(pages).sort((a, b) => a - b);
    })();

    return (
        <>
            {preview?.text && (
                <TextPreviewModal
                    text={preview.text}
                    title={preview.title}
                    onClose={() => setPreview(null)}
                />
            )}

            <div className="list-kpi-row">
                <div className="card list-kpi sent">
                    <div className="list-kpi-body">
                        <div>
                            <p className="list-kpi-label">Sent</p>
                            <h6 className="list-kpi-value">{sentCount}</h6>
                            <div className="list-kpi-actions">
                                <button type="button" className="list-kpi-btn view" onClick={() => openFilter("1")}>
                                    View
                                </button>
                                <button
                                    type="button"
                                    className="list-kpi-btn clear"
                                    onClick={() => removeMessageLog(null, "sent")}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div className="list-kpi-icon">
                            <Icon icon="mdi:check-circle-outline" />
                        </div>
                    </div>
                </div>
                <div className="card list-kpi invalid">
                    <div className="list-kpi-body">
                        <div>
                            <p className="list-kpi-label">Failed / Invalid</p>
                            <h6 className="list-kpi-value">{invalidCount}</h6>
                            <div className="list-kpi-actions">
                                <button type="button" className="list-kpi-btn view" onClick={() => openFilter("0")}>
                                    View
                                </button>
                                <button
                                    type="button"
                                    className="list-kpi-btn clear"
                                    onClick={() => removeMessageLog(null, "invalid")}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div className="list-kpi-icon">
                            <Icon icon="mdi:alert-circle-outline" />
                        </div>
                    </div>
                </div>
                <div className="card list-kpi queue">
                    <div className="list-kpi-body">
                        <div>
                            <p className="list-kpi-label">In queue</p>
                            <h6 className="list-kpi-value">{queueCount}</h6>
                            <div className="list-kpi-actions">
                                <button type="button" className="list-kpi-btn view" onClick={() => openFilter("2")}>
                                    View
                                </button>
                                <button
                                    type="button"
                                    className="list-kpi-btn clear"
                                    onClick={() => removeMessageLog(null, "queue")}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div className="list-kpi-icon">
                            <Icon icon="mdi:timer-sand" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="card basic-data-table message-log-card list-card">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div>
                                <h5 className="card-title mb-0">Delivery log</h5>
                                <p className="mb-0 text-secondary-light text-sm">
                                    {liveMode
                                        ? "Live delivery feed · scroll sideways for status, reason, dates"
                                        : `Filtered: ${FILTER_LABELS[currentFilter] || "Custom"}`}
                                </p>
                            </div>
                            <span className={`list-status ${wsConnected ? "success" : "danger"}`}>
                                {wsConnected ? "Live" : "Offline"}
                            </span>
                            {currentFilter && (
                                <button type="button" className="log-filter-chip" onClick={clearFilter}>
                                    {FILTER_LABELS[currentFilter]}
                                    <Icon icon="mdi:close" width={14} />
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                            onClick={clearFilter}
                            title="Back to live feed"
                        >
                            <Icon
                                icon="material-symbols:refresh"
                                width={18}
                                style={{
                                    transition: "transform 0.6s linear",
                                    transform: isSpinning ? "rotate(360deg)" : "rotate(0deg)",
                                }}
                            />
                            Live feed
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <select
                                value={limit}
                                onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
                                className="form-select radius-8"
                                style={{ width: 80 }}
                            >
                                {[10, 20, 50, 100].map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                            <span className="text-secondary-light text-sm">entries per page</span>
                        </div>
                        <input
                            type="search"
                            className="form-control radius-8"
                            style={{ maxWidth: 260 }}
                            placeholder="Search mobile, message, reason…"
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </div>

                    <div className="table-responsive message-log-table-wrap">
                        <table className="table bordered-table mb-0 message-log-table">
                            <thead>
                                <tr>
                                    {columns.map((col, index) => (
                                        <th key={index} className={col.className || ""}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center text-muted py-4">
                                            {liveMode ? (
                                                "Waiting for live logs…"
                                            ) : currentFilter === "2" && queueCount > 0 ? (
                                                <div className="log-empty-hint">
                                                    <strong style={{ color: "#111b21" }}>
                                                        Queue count is {queueCount}, but no queue rows were returned.
                                                    </strong>
                                                    <p className="mb-0 mt-1">
                                                        Backend must return rows for{" "}
                                                        <code>status=2</code> on{" "}
                                                        <code>user/get-messages-log</code>.
                                                    </p>
                                                </div>
                                            ) : (
                                                "No logs for this filter."
                                            )}
                                        </td>
                                    </tr>
                                ) : (
                                    pageRows.map((row, rowIndex) => (
                                        <tr key={row.id || rowIndex}>
                                            {columns.map((col, colIndex) => (
                                                <td key={colIndex} className={col.className || ""}>
                                                    {col.render
                                                        ? col.render(row[col.key], row)
                                                        : row[col.key]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="pagination-controls d-flex flex-wrap align-items-center gap-2 justify-content-between mt-24">
                        <span className="pagination-meta">
                            Showing {startIndex} to {endIndex} of {totalRecords} entries
                        </span>
                        <ul className="pagination d-flex flex-wrap align-items-center gap-2 mb-0">
                            <li className="page-item">
                                <button
                                    type="button"
                                    onClick={() => handlePageChange(Math.max(1, safePage - 1))}
                                    disabled={safePage === 1}
                                    className="page-link"
                                    aria-label="Previous page"
                                >
                                    <Icon icon="ep:d-arrow-left" className="text-xl" />
                                </button>
                            </li>
                            {pageButtons.map((p, idx) => {
                                const prev = pageButtons[idx - 1];
                                const showEllipsis = prev && p - prev > 1;
                                const active = p === safePage;
                                return (
                                    <React.Fragment key={p}>
                                        {showEllipsis && (
                                            <li className="page-item">
                                                <span className="px-1 pagination-meta">…</span>
                                            </li>
                                        )}
                                        <li className="page-item">
                                            <button
                                                type="button"
                                                onClick={() => handlePageChange(p)}
                                                className={`page-link ${active ? "is-active" : ""}`}
                                                aria-current={active ? "page" : undefined}
                                            >
                                                {p}
                                            </button>
                                        </li>
                                    </React.Fragment>
                                );
                            })}
                            <li className="page-item">
                                <button
                                    type="button"
                                    onClick={() =>
                                        handlePageChange(Math.min(totalPages, safePage + 1))
                                    }
                                    disabled={safePage >= totalPages}
                                    className="page-link"
                                    aria-label="Next page"
                                >
                                    <Icon icon="ep:d-arrow-right" className="text-xl" />
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MessageLogLayer;
